/**
 * Logger utility for conditional logging based on environment
 * Prevents console logs in production while maintaining development debugging
 */

type LogLevel = 'log' | 'error' | 'warn' | 'info' | 'debug';

interface LoggerInterface {
  log: (...args: any[]) => void;
  error: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  info: (...args: any[]) => void;
  debug: (...args: any[]) => void;
}

class Logger implements LoggerInterface {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development' || 
                        process.env.NODE_ENV === 'dev' ||
                        import.meta.env.DEV;
  }

  private createLogMethod(level: LogLevel) {
    return (...args: any[]) => {
      if (this.isDevelopment) {
        console[level](...args);
      }
    };
  }

  log = this.createLogMethod('log');
  error = this.createLogMethod('error');
  warn = this.createLogMethod('warn');
  info = this.createLogMethod('info');
  debug = this.createLogMethod('debug');

  // Method for production-safe logging with optional override
  safeLog(level: LogLevel, ...args: any[]) {
    if (this.isDevelopment) {
      console[level](...args);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export individual methods for convenience
export const log = (...args: any[]) => logger.log(...args);
export const error = (...args: any[]) => logger.error(...args);
export const warn = (...args: any[]) => logger.warn(...args);
export const info = (...args: any[]) => logger.info(...args);
export const debug = (...args: any[]) => logger.debug(...args);

export default logger;
